/**
 * SUB-4.7 · FR74, AC1. Realtime subscriptions that follow the session's scope.
 *
 * > Given a merged list subscribed to realtime changes, when events are
 * > delivered, then channels span the subtree and never deliver events from
 * > outside it.
 *
 * Every rule below was read out of the live `realtime` schema on 2026-08-13
 * (`realtime.apply_rls`, `realtime.build_prepared_statement_sql`,
 * `realtime.is_visible_through_filters`), not from Supabase's documentation.
 * `supabase/tests/verify_sub_4_7_adjacent_mechanisms.sql` Part B measures the
 * three of them that are behaviour.
 *
 * ---------------------------------------------------------------------------
 * 1 · THE FENCE IS THE POLICY, AND IT IS RE-EVALUATED PER EVENT.
 *
 * For every change on an RLS-enabled published table, `realtime.apply_rls`
 * prepares `select exists(select 1 from <table> where <pk>=<value>)`, then for
 * each subscription sets `role` to that subscriber's `claims_role` and
 * `request.jwt.claims` to the claims captured when they subscribed, and runs it.
 * `auth.uid()` reads `request.jwt.claims ->> 'sub'`, so the caller's identity is
 * real, `get_current_user_tenant_id()` re-reads the session row, and
 * `merged_scope_tenant_ids()` re-derives the set ON THAT EVENT.
 *
 * Two consequences, and both are load-bearing:
 *
 *   - A merged subscription spans the subtree with NO code, because
 *     `projects_select` and `tasks_select` resolve through
 *     `merged_scope_tenant_ids()`. There is no channel-per-account to build.
 *   - A scope change needs NO resubscribe. Revoke the grant and the very next
 *     event is filtered by the narrowed set. The channel name below still moves,
 *     for the reason in section 3, but delivery does not depend on it.
 *
 * ---------------------------------------------------------------------------
 * 2 · ⚠️ A CLIENT `filter:` IS APPLIED BEFORE RLS, SO IT CAN ONLY NARROW.
 *
 * `realtime.is_visible_through_filters` runs first and the RLS check runs only
 * on what survives it. So `filter: 'tenant_id=eq.<anchor>'` on a merged list is
 * not a redundant safety belt, it is a silent bug: every sub-account event is
 * discarded before the policy that was going to allow it is ever consulted, and
 * the merged list stops updating for twelve of thirteen accounts while looking
 * completely healthy. Nothing errors and nothing is logged.
 *
 * That is why `realtimeFilterFor` refuses to produce a tenant filter for a
 * scope-governed table rather than leaving it to a reviewer to notice.
 *
 * ---------------------------------------------------------------------------
 * 3 · ⚠️ DELETE IS NOT RLS-CHECKED AT ALL.
 *
 * `apply_rls` reads, verbatim:
 *
 *     if not is_rls_enabled or action = 'DELETE' then
 *         visible_role_sub_ids = visible_role_sub_ids || subscription_id;
 *
 * The row is gone by then, so `select exists(... where id=…)` could only ever
 * answer false, and the platform chooses to fan out rather than to suppress.
 * EVERY subscriber of `public:projects` receives EVERY delete on `projects`,
 * from every tenant on the platform.
 *
 * ⚠️ CORRECTED 2026-08-18 (SUB-1.32). THIS PARAGRAPH USED TO SAY THE PAYLOAD
 * MITIGATES IT — "for an RLS-enabled table `old_record` is filtered to primary
 * key columns, so what crosses the boundary is a uuid". **That is true of most
 * tables and false of the ones that matter most.** What decides it is
 * `REPLICA IDENTITY`, not RLS. Measured across all 19 tables in the
 * `supabase_realtime` publication:
 *
 *     17  default (PK only)   -> `old` carries the primary key, as described
 *      2  FULL (all columns)  -> `old` carries EVERY COLUMN
 *
 * The two are `project_messages` and `solar_proposal_jobs`. For those, a delete
 * fans the complete row out to every subscriber, unfiltered by RLS. Chat message
 * bodies are in that set.
 *
 * ⚠️ What actually fences those two today is the client `filter:` — and only
 * because REPLICA IDENTITY FULL puts `tenant_id` in `old_record` where
 * `is_visible_through_filters` can see it. On a default-identity table a filter
 * on a non-PK column matches nothing on a DELETE, so it drops every event
 * instead. **The safety of a delete therefore depends on a per-table storage
 * setting that no consumer can see from its own code**, which is why the rule
 * below is absolute rather than conditional.
 *
 * This is Supabase platform code in the `realtime` schema, it is replaced on
 * every Realtime upgrade, and it predates sub-accounts by the whole life of the
 * app. Merged scope neither causes nor widens it. It is recorded as F-44 and it
 * is the one clause of AC1 that is not satisfied.
 *
 * The rule it forces on every consumer: TREAT AN EVENT AS AN INVALIDATION
 * SIGNAL, NEVER AS DATA. Refetch through the normal query path, which is
 * RLS-bounded on the read. `payloadIsRlsChecked` states which events that is
 * merely good practice for and which one it is the only defence for.
 *
 * ⚠️ AND IT IS ENFORCED NOW, WHICH IT WAS NOT. `payloadIsRlsChecked` was exported
 * by SUB-4.7 and called by NOBODY, while seven files were already reading
 * `payload.old`. A convention with no enforcement is a comment.
 * `lib/realtimeDeletePayload.test.ts` (SUB-1.32 / F-44) fails the build when a
 * consumer reads anything off a delete payload beyond its primary key.
 */

import type { MergedScopeStatus } from '@/lib/mergedScope';
import { scopeReachKey } from '@/lib/mergedScope';

/**
 * The tables whose SELECT policy resolves through `merged_scope_tenant_ids()`.
 *
 * Measured, not assumed: on 2026-08-13 exactly two policies in the live catalog
 * referenced that function, `projects_select` and `tasks_select`. Everything
 * else on the merged surfaces, `pipelines` and `stages` included, is still
 * single-tenant by SUB-4.8's D23.
 *
 * ⚠️ ADD TO THIS LIST IN THE SAME COMMIT AS THE MIGRATION THAT WIDENS THE
 * POLICY, never before and never after. A table listed here whose policy is
 * still single-tenant loses its tenant filter and gains nothing in return; a
 * table widened but not listed here keeps a filter that now silently drops the
 * events the widening was for.
 */
export const SCOPE_GOVERNED_REALTIME_TABLES = ['projects', 'tasks'] as const;

export type ScopeGovernedTable = (typeof SCOPE_GOVERNED_REALTIME_TABLES)[number];

export function isScopeGoverned(table: string): table is ScopeGovernedTable {
  return (SCOPE_GOVERNED_REALTIME_TABLES as readonly string[]).includes(table);
}

/**
 * The `filter:` string for a `postgres_changes` binding, or `undefined` for none.
 *
 * ⚠️ SCOPE-GOVERNED TABLES ALWAYS GET `undefined`, IN BOTH SCOPES. Section 2 is
 * why it cannot be conditional on the current mode: the filter is chosen when
 * the channel is built and the scope can narrow or widen underneath it at any
 * moment, server-side, with no client event. A subscription that was correct
 * when it was created would then be quietly wrong, which is the failure mode
 * this whole module is about. The policy is already the fence in single scope,
 * so dropping the filter costs nothing and removes the way to get it wrong.
 *
 * Tables that are NOT scope-governed keep their single-tenant filter, because
 * for them it matches the policy and it saves a wakeup per event per tab.
 */
export function realtimeFilterFor(
  table: string,
  tenantId: string | null | undefined,
  column: string = 'tenant_id',
): string | undefined {
  if (isScopeGoverned(table)) return undefined;
  if (!tenantId) return undefined;
  return `${column}=eq.${tenantId}`;
}

/**
 * A channel name that changes when the session's reach changes.
 *
 * Delivery does not need this: section 1 shows the policy is re-evaluated per
 * event, so an existing channel narrows and widens on its own. Two other things
 * do need it.
 *
 * ⚠️ A CHANNEL NAME IS A CACHE KEY IN DISGUISE. `supabase.channel(name)` returns
 * the EXISTING channel for a name that is already joined, so a fixed name shared
 * across a scope change hands the second subscriber the first one's callbacks,
 * still closed over the previous scope's `queryClient` keys and component state.
 *
 * ⚠️ AND IT IS THE ONLY THING THAT MAKES A LEAK LEGIBLE. When two tabs of the
 * same user sit in different scopes, identically named channels are one channel
 * in the browser and indistinguishable in the Realtime inspector. Naming them
 * apart is what turns "the merged list updated when it should not have" from a
 * guess into something readable.
 */
export function scopedChannelName(base: string, status: MergedScopeStatus): string {
  return `${base}:scope=${scopeReachKey(status)}`;
}

/**
 * Whether the platform checked this event against RLS before delivering it.
 *
 * `false` for DELETE, per section 3. A consumer must never render the payload of
 * an event for which this is false, nor infer from its arrival that the record
 * was one of theirs: use it to refetch and let the read path answer.
 */
export function payloadIsRlsChecked(eventType: string): boolean {
  return eventType.toUpperCase() !== 'DELETE';
}
